/**
 * „Installation“ in der Produktverwaltung — die installierte Basis des Herstellers: alle Assets
 * seiner Produkte, auch bei anderen Mandanten (tri-server api/installedBase.ts).
 *
 * Filter nach Produkt und Zustand; zwei Ansichten: Tabelle (Produkt, Gerät, Owner mit Branche,
 * Lage, Zustand) und Karte (InstalledBaseMap: Punkte nach Zustand, herausgezoomt Kreisdiagramme).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import type { Column, ColumnDef, SortingState } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, Award, Box } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProductPageLink } from "../components/ProductPageLink";
import { Popup } from "react-map-gl";

import { authenticationAtom } from "../State";
import { configuration } from "../Configuration";
import { authenticatedFetch } from "../JWTAuthService";
import { useModalContext } from "../components/ModalProvider";
import InstalledBaseMap, {
  INSTALLATION_COLOR,
  OPERATING_COLOR,
  OPERATING_STATES,
  type InstallationState,
  type OperatingStateName,
  type Point,
} from "./InstalledBaseMap";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface InstalledAsset {
  id: string;
  businessId: string | null;
  name: string | null;
  serialNumber: string | null;
  iot: { id: string | null; deviceType: string | null; gateway: string | null; configuration: Record<string, unknown> } | null;
  productId: string;
  productName: string | null;
  owner: { name: string | null; company: string | null; legalForm: string | null; industry: string | null } | null;
  address: string | null;
  lat: number | null;
  lon: number | null;
  state: InstallationState;
  operatingState: OperatingStateName;
  activated: string | null;
  commissioned: string | null;
}

interface InstalledBasePage {
  manufacturer: boolean;
  products: { id: string; name: string }[];
  items: InstalledAsset[];
  total: number;
  counts: Partial<Record<InstallationState, number>>;
  operatingCounts: Partial<Record<OperatingStateName, number>>;
}

const ALL = "all";

const PAGE_SIZE = 25;

const get = async (path: string) => {
  const response = await authenticatedFetch(`${configuration.serverUrl}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.json();
};

/** Name/value rows of an IoT configuration; nested values get a dotted path. */
export const flattenConfiguration = (value: unknown, prefix = ""): Array<[string, string]> => {
  if (value === null || value === undefined) return prefix ? [[prefix, "—"]] : [];
  if (Array.isArray(value)) {
    if (!value.length) return [[prefix, "[]"]];
    return value.flatMap((item, index) => flattenConfiguration(item, `${prefix}[${index}]`));
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return prefix ? [[prefix, "{}"]] : [];
    return entries.flatMap(([key, inner]) => flattenConfiguration(inner, prefix ? `${prefix}.${key}` : key));
  }
  return [[prefix, String(value)]];
};

const filterQuery = (product: string, state: string, operating: string, search: string) =>
  [
    search.trim() ? `q=${encodeURIComponent(search.trim())}` : "",
    product !== ALL ? `product=${encodeURIComponent(product)}` : "",
    state !== ALL ? `state=${state}` : "",
    operating !== ALL ? `operatingState=${operating}` : "",
  ]
    .filter(Boolean)
    .join("&");

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "—");

/** Clickable column header; the server sorts the whole result (the table pages on the server). */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: React.ReactNode }) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

export default function InstalledBasePanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom<any>(authenticationAtom);
  const [data, setData] = useState<InstalledBasePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(ALL);
  const [state, setState] = useState(ALL);
  const [operating, setOperating] = useState(ALL);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [iotAsset, setIotAsset] = useState<InstalledAsset | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const sortParams = sorting[0] ? `sortBy=${sorting[0].id}&sortDir=${sorting[0].desc ? "desc" : "asc"}` : "";
  const [page, setPage] = useState(1);
  const [view, setView] = useState("table");
  const [points, setPoints] = useState<Point[] | null>(null);
  const [withoutLocation, setWithoutLocation] = useState(0);
  const [selected, setSelected] = useState<InstalledAsset | null>(null);
  const customer = authentication?.customer;
  const query = filterQuery(product, state, operating, search);

  // Free search: the server filters once typing pauses.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
        setSelected(null);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // One page from the server — every device is reachable, none is loaded that is not shown.
  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    setLoading(true);
    get(
      `/customers/${customer}/installed-base?${[query, sortParams, `skip=${(page - 1) * PAGE_SIZE}`, `limit=${PAGE_SIZE}`]
        .filter(Boolean)
        .join("&")}`,
    )
      .then((result) => !cancelled && setData(result))
      .catch((error) => !cancelled && pushError(error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, query, sortParams, page]);

  // All points for the map, loaded when the map is opened (and again after a filter change).
  useEffect(() => {
    setPoints(null);
  }, [query]);
  useEffect(() => {
    if (!customer || view !== "map" || points) return;
    let cancelled = false;
    get(`/customers/${customer}/installed-base/points${query ? `?${query}` : ""}`)
      .then((result) => {
        if (cancelled) return;
        setPoints(result.points ?? []);
        setWithoutLocation(result.withoutLocation ?? 0);
      })
      .catch((error) => !cancelled && pushError(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, view, points, query]);

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
    setSelected(null);
  };

  const operatingCounts = data?.operatingCounts ?? {};
  const counts: Record<InstallationState, number> = {
    commissioned: data?.counts?.commissioned ?? 0,
    activated: data?.counts?.activated ?? 0,
    registered: data?.counts?.registered ?? 0,
  };

  const openAsset = async (id: string) => {
    if (!customer) return;
    try {
      setSelected(await get(`/customers/${customer}/installed-base/assets/${encodeURIComponent(id)}`));
    } catch (error) {
      pushError(error);
    }
  };

  const columns: ColumnDef<InstalledAsset>[] = [
    {
      id: "product",
      accessorFn: (row: InstalledAsset) => row.productName ?? "",
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.product")}</SortableHeader>,
      cell: ({ row }) =>
        row.original.productName ? (
          <ProductPageLink product={row.original.productId}>{row.original.productName}</ProductPageLink>
        ) : (
          "—"
        ),
    },
    {
      id: "device",
      accessorFn: (row: InstalledAsset) => row.name ?? row.serialNumber ?? row.id,
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.device")}</SortableHeader>,
      cell: ({ row }) => row.original.name || row.original.serialNumber || row.original.id,
    },
    {
      id: "iot",
      accessorFn: (row: InstalledAsset) => row.iot?.id ?? "",
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.iot")}</SortableHeader>,
      cell: ({ row }) =>
        row.original.iot?.id ? (
          <Button
            variant="link"
            className="h-auto p-0 font-mono text-xs"
            onClick={(event) => {
              event.stopPropagation();
              setIotAsset(row.original);
            }}
          >
            {row.original.iot.id}
          </Button>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "owner",
      accessorFn: (row: InstalledAsset) => row.owner?.name ?? "",
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.owner")}</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="min-w-0 break-words">{row.original.owner?.name ?? "—"}</span>
          {row.original.owner?.industry ? (
            <span className="min-w-0 break-words text-xs text-muted-foreground">{row.original.owner.industry}</span>
          ) : null}
        </div>
      ),
    },
    { id: "location",
      accessorFn: (row: InstalledAsset) => row.address ?? "", header: ({ column }) => <SortableHeader column={column}>{t("installedBase.location")}</SortableHeader>, cell: ({ row }) => row.original.address ?? (row.original.lat !== null ? `${row.original.lat?.toFixed(5)}, ${row.original.lon?.toFixed(5)}` : "—") },
    {
      id: "operatingState",
      accessorFn: (row: InstalledAsset) => row.operatingState,
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.operatingState")}</SortableHeader>,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: OPERATING_COLOR[row.original.operatingState] }} />
          {t(`installedBase.operatingStates.${row.original.operatingState}`)}
        </span>
      ),
    },
    {
      id: "state",
      accessorFn: (row: InstalledAsset) => row.state,
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.state")}</SortableHeader>,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
          <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: INSTALLATION_COLOR[row.original.state] }} />
          {t(`installedBase.states.${row.original.state}`)}
        </span>
      ),
    },
    {
      id: "since",
      accessorFn: (row: InstalledAsset) => row.commissioned ?? row.activated ?? "",
      header: ({ column }) => <SortableHeader column={column}>{t("installedBase.since")}</SortableHeader>,
      cell: ({ row }) => formatDate(row.original.commissioned ?? row.original.activated),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            // The certificate exists only for IoT devices (server: 404 without iotDeviceType).
            disabled={!row.original.iot}
            title={t("installedBase.openCertificate")}
            aria-label={t("installedBase.openCertificate")}
            onClick={() => window.open(`${configuration.serverUrl}/public/certificates/asset/${row.original.id}`, "_blank", "noopener")}
          >
            <Award className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={t("installedBase.openAsset")}
            aria-label={t("installedBase.openAsset")}
            onClick={() => navigate(`/entity?selectedEntityType=Asset&selectedEntity=${row.original.id}`)}
          >
            <Box className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (!loading && data && !data.manufacturer) {
    return <p className="text-sm text-muted-foreground">{t("installedBase.noProducts")}</p>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="m-0 min-w-0 break-words text-sm text-muted-foreground">{t("installedBase.intro")}</p>
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="installed-base-search">{t("installedBase.search")}</Label>
          <Input
            id="installed-base-search"
            className="w-[280px] max-w-full"
            value={searchInput}
            placeholder={t("installedBase.searchPlaceholder")}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label>{t("installedBase.product")}</Label>
          <Select value={product} onValueChange={changeFilter(setProduct)}>
            <SelectTrigger className="w-[260px] max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("installedBase.allProducts")}</SelectItem>
              {(data?.products ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label>{t("installedBase.operatingState")}</Label>
          <Select value={operating} onValueChange={changeFilter(setOperating)}>
            <SelectTrigger className="w-[220px] max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("installedBase.allStates")}</SelectItem>
              {OPERATING_STATES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`installedBase.operatingStates.${s}`)} ({(operatingCounts[s] ?? 0).toLocaleString()})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label>{t("installedBase.state")}</Label>
          <Select value={state} onValueChange={changeFilter(setState)}>
            <SelectTrigger className="w-[220px] max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("installedBase.allStates")}</SelectItem>
              {(["commissioned", "activated", "registered"] as InstallationState[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`installedBase.states.${s}`)} ({counts[s].toLocaleString()})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="pb-2 text-sm text-muted-foreground">
          {t("installedBase.count", { count: data?.total ?? 0, formatted: (data?.total ?? 0).toLocaleString() })}
        </span>
      </div>

      <Tabs value={view} onValueChange={setView} className="min-w-0">
        <TabsList>
          <TabsTrigger value="table">{t("installedBase.table")}</TabsTrigger>
          <TabsTrigger value="map">{t("installedBase.map")}</TabsTrigger>
        </TabsList>
        <TabsContent value="table">
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            pageSize={PAGE_SIZE}
            page={page}
            onPageChange={setPage}
            totalRows={data?.total ?? 0}
            sorting={sorting}
            onSortingChange={(next) => {
              setSorting(next);
              setPage(1);
            }}
            loading={loading}
            emptyMessage={t("installedBase.empty")}
          />
        </TabsContent>
        <TabsContent value="map">
          <div className="h-[560px] min-w-0 overflow-hidden rounded-md border border-border">
            {view === "map" && points ? (
              <InstalledBaseMap key={query} points={points} onSelectDevice={openAsset}>
                {selected && selected.lat !== null && selected.lon !== null ? (
                  <Popup
                    longitude={selected.lon}
                    latitude={selected.lat}
                    onClose={() => setSelected(null)}
                    closeOnClick={false}
                    maxWidth="280px"
                  >
                    <div className="flex flex-col gap-0.5 text-xs">
                      <strong>
                        {selected.productName ? (
                          <ProductPageLink product={selected.productId}>{selected.productName}</ProductPageLink>
                        ) : (
                          "—"
                        )}
                      </strong>
                      <span>{selected.name || selected.serialNumber || selected.id}</span>
                      <span>{selected.owner?.name ?? "—"}</span>
                      {selected.owner?.industry ? <span className="text-muted-foreground">{selected.owner.industry}</span> : null}
                      {selected.address ? <span>{selected.address}</span> : null}
                      <span style={{ color: OPERATING_COLOR[selected.operatingState] }}>
                        <strong>{t(`installedBase.operatingStates.${selected.operatingState}`)}</strong>
                      </span>
                      <span>{t(`installedBase.states.${selected.state}`)} · {formatDate(selected.commissioned ?? selected.activated)}</span>
                    </div>
                  </Popup>
                ) : null}
              </InstalledBaseMap>
            ) : view === "map" ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t("installedBase.loadingMap")}
              </div>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
            {OPERATING_STATES.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: OPERATING_COLOR[s] }} />
                {t(`installedBase.operatingStates.${s}`)}
              </span>
            ))}
            {points ? <span>{t("installedBase.withoutLocation", { count: withoutLocation })}</span> : null}
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!iotAsset} onOpenChange={(open) => !open && setIotAsset(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{t("installedBase.iotTitle", { id: iotAsset?.iot?.id ?? "" })}</DialogTitle>
          </DialogHeader>
          {iotAsset?.iot ? (
            <table className="w-full text-sm">
              <tbody>
                {[
                  ...(iotAsset.iot.deviceType ? [[t("installedBase.iotDeviceType"), iotAsset.iot.deviceType] as [string, string]] : []),
                  ...(iotAsset.iot.gateway ? [[t("installedBase.iotGateway"), iotAsset.iot.gateway] as [string, string]] : []),
                  ...flattenConfiguration(iotAsset.iot.configuration),
                ].map(([name, value]) => (
                  <tr key={name} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-4 align-top font-medium">{name}</td>
                    <td className="break-all py-1.5 font-mono text-xs">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
